/**
 * DocumentosSemVisitaQuadro — o painel explica os documentos que não viraram visita (ADR-034,
 * spec termos-sem-visita Req 3; design §4.4).
 *
 * - A conta: "N caminhos (D duplicados) → X documentos = V visitas + S sem visita".
 * - Motivo × quantidade; "ver arquivos" abre a lista do motivo (caminho, página, detalhe em
 *   texto, link para a pasta), paginada.
 * - Para o administrador, as linhas "outro documento" e "falha na extração" oferecem "Reler com
 *   o prompt novo": a confirmação mostra a quantidade e o custo estimado (GET /lotes/releitura)
 *   e só então dispara (POST). Os dois motivos entram na mesma releitura.
 *
 * Acesso de leitura de todos os perfis, com o polling dos totais (Req 3 AC5). Nenhum dado
 * pessoal: o detalhe é técnico (RN-GLOBAL-004).
 *
 * ADR-048 (spec evidencias-complementares, design §5): a evidência complementar ligada a uma visita sai de "sem visita"
 * e entra na conta à parte ("… = V visitas + E evidências ligadas a visitas + S sem visita"); a sem ligação é o motivo
 * "Evidência sem visita", com o tipo em palavras na lista. Numa API anterior (sem esses campos), o quadro de antes.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { hasAnyProfile, useAuth } from '../contexts/AuthContext';
import { useArquivosSemVisita, useSemVisita } from '../hooks/usePainel';
import { useEstimativaReleitura, useReleitura } from '../hooks/useLotes';
import { ArquivoSemVisita, DocumentosSemVisita, MOTIVOS_SEM_VISITA, MotivoSemVisita } from '../services/painel-types';
import { ehEvidenciaComplementar, rotuloMotivoFalha, rotuloTipoDocumento } from './format';
import { formatarUsdDocumento } from '../pages/LotesPage';

const numero = new Intl.NumberFormat('pt-BR');

/** Rótulos do Req 3 AC1. */
export const ROTULO_MOTIVO: Readonly<Record<MotivoSemVisita, string>> = {
  outroDocumento: 'Outro documento',
  declaracao: 'Declaração',
  evidenciaSemVisita: 'Evidência sem visita',
  formatoNaoAceito: 'Formato não aceito',
  falhaPreprocessamento: 'Falha no pré-processamento',
  paginasAlemDoLimite: 'Páginas além do limite',
  falhaExtracao: 'Falha na extração',
  aguardando: 'Aguardando processamento',
};

/** Motivos que a releitura dos "outro documento" cobre (design §3.1). */
const MOTIVOS_RELEITURA: ReadonlySet<MotivoSemVisita> = new Set(['outroDocumento', 'falhaExtracao']);

const DETALHES: Readonly<Record<string, string>> = {
  OUTRO: 'lido como outro documento',
  ILEGIVEL: 'ilegível',
  DECLARACAO_RESPONSABILIDADE: 'declaração / termo de responsabilidade',
  TERMO_ADESAO: 'termo lido; gravando a visita',
  PENDENTE: 'pré-processamento em andamento',
  pendente: 'na fila da extração',
  enviado: 'em extração',
  FALHA_DERIVADO: 'erro ao preparar a imagem',
  FALHA_ARQUIVO: 'o arquivo não abre',
  FALHA_PAGINA: 'falha na página',
  NAO_PROCESSADA_LIMITE: 'além do limite de páginas por arquivo',
};

/** Detalhe técnico → texto (os códigos do pré-processamento usam os rótulos da carga). */
export function textoDetalhe(motivo: MotivoSemVisita, detalhe: string | null): string {
  if (detalhe === null) {
    return motivo === 'aguardando' ? 'aguardando a extração' : '—';
  }
  if (DETALHES[detalhe]) {
    return DETALHES[detalhe];
  }
  // ADR-048: a classe da evidência complementar, em palavras.
  if (ehEvidenciaComplementar(detalhe)) {
    return rotuloTipoDocumento(detalhe);
  }
  if (motivo === 'formatoNaoAceito') {
    return `formato ${detalhe}`;
  }
  if (motivo === 'falhaPreprocessamento') {
    return rotuloMotivoFalha(detalhe);
  }
  return detalhe;
}

/** Link para a pasta do arquivo (Pastas preserva o caminho literal). */
function linkPasta(caminho: string): string {
  const partes = caminho.split('/').filter((p) => p.length > 0);
  partes.pop();
  return partes.length === 0 ? '/pastas' : `/pastas/${partes.map(encodeURIComponent).join('/')}`;
}

function ListaArquivos({ motivo, onFechar }: { motivo: MotivoSemVisita; onFechar: () => void }): JSX.Element {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useArquivosSemVisita(motivo);
  const itens: readonly ArquivoSemVisita[] = data?.pages.flatMap((p) => p.itens) ?? [];
  return (
    <div className="sem-visita-lista" aria-label={`Arquivos: ${ROTULO_MOTIVO[motivo]}`}>
      <div className="row-actions">
        <h3>{ROTULO_MOTIVO[motivo]}</h3>
        <button type="button" className="btn btn-ghost" onClick={onFechar}>
          Fechar lista
        </button>
      </div>
      {isLoading && <p className="muted">Carregando…</p>}
      {isError && <p className="notice">Não foi possível carregar os arquivos.</p>}
      {!isLoading && !isError && itens.length === 0 && <p className="muted">Nenhum arquivo.</p>}
      {itens.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Arquivo</th>
              <th>Página</th>
              <th>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((a) => (
              <tr key={`${a.caminhoOriginal}:${a.pagina ?? ''}`}>
                <td>
                  <Link to={linkPasta(a.caminhoOriginal)}>{a.caminhoOriginal}</Link>
                  {a.outrosCaminhos > 0 && (
                    <span className="muted"> (+{numero.format(a.outrosCaminhos)} caminho(s) com o mesmo conteúdo)</span>
                  )}
                </td>
                <td>{a.pagina ?? '—'}</td>
                <td>{textoDetalhe(a.motivo, a.detalhe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {hasNextPage && (
        <button type="button" className="btn" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
        </button>
      )}
    </div>
  );
}

function ConfirmarReleitura({ onFechar }: { onFechar: () => void }): JSX.Element {
  const estimativa = useEstimativaReleitura(true);
  const releitura = useReleitura();
  const e = estimativa.data;
  const r = releitura.data;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Reler com o prompt novo">
      <div className="modal">
        <h2>Reler com o prompt novo</h2>
        {estimativa.isLoading && <p className="muted">Calculando…</p>}
        {estimativa.isError && <p className="notice">Não foi possível calcular a releitura.</p>}
        {e && !r && e.documentos === 0 && <p>Não há documentos para reler.</p>}
        {e && !r && e.documentos > 0 && (
          <>
            <p>
              Reler <strong>{numero.format(e.documentos)}</strong> documento(s) sem visita — lidos como outro documento
              ou com falha na extração — com o prompt <span className="mono">{e.versaoPrompt}</span>. Termos,
              declarações e documentos com visita não são relidos.
            </p>
            <p>
              Custo estimado:{' '}
              {e.custoEstimadoUsd === null ? 'sem histórico de custo' : formatarUsdDocumento(e.custoEstimadoUsd)}.
            </p>
          </>
        )}
        {releitura.isError && <p className="notice">Não foi possível disparar a releitura. Tente novamente.</p>}
        {r && r.loteId === null && <p>Não há documentos para reler.</p>}
        {r && r.loteId !== null && (
          <p className="notice notice-info">
            Releitura disparada: {numero.format(r.documentos)} documento(s). Acompanhe em{' '}
            <Link to="/admin/lotes">Lotes</Link>.
          </p>
        )}
        <div className="row-actions">
          {!r && e && e.documentos > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={releitura.isPending}
              onClick={() => releitura.mutate()}
            >
              {releitura.isPending ? 'Disparando…' : 'Confirmar releitura'}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onFechar}>
            {r ? 'Fechar' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DocumentosSemVisitaQuadro(): JSX.Element {
  const auth = useAuth();
  const ehAdmin = hasAnyProfile(auth, ['admin']);
  const { conta, carregando, erro } = useSemVisita();
  const [aberto, setAberto] = useState<MotivoSemVisita | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <section className="card sem-visita" aria-labelledby="titulo-sem-visita">
      <h2 id="titulo-sem-visita">Documentos sem visita</h2>
      {carregando && <p className="muted">Carregando…</p>}
      {erro && <p className="notice">Quadro temporariamente indisponível. Tentaremos novamente.</p>}
      {conta && (
        <>
          <p className="sem-visita-conta">{linhaDaConta(conta)}</p>
          <p className="muted">
            O mesmo conteúdo em mais de um caminho conta um documento só; cada página de um PDF é um documento.
          </p>
          {conta.planilhas && totalDePlanilhas(conta.planilhas) > 0 && (
            <p className="muted">{linhaDasPlanilhas(conta.planilhas)}</p>
          )}
          <table className="table">
            <thead>
              <tr>
                <th>Motivo</th>
                <th>Documentos</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {motivosDaConta(conta).map(([m, qtd]) => (
                <tr key={m}>
                  <td>{ROTULO_MOTIVO[m]}</td>
                  <td>{numero.format(qtd)}</td>
                  <td className="row-actions">
                    {qtd > 0 && (
                      <button type="button" className="btn btn-ghost" onClick={() => setAberto(m)}>
                        Ver arquivos
                      </button>
                    )}
                    {ehAdmin && MOTIVOS_RELEITURA.has(m) && qtd > 0 && (
                      <button type="button" className="btn" onClick={() => setConfirmando(true)}>
                        Reler com o prompt novo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {aberto && <ListaArquivos motivo={aberto} onFechar={() => setAberto(null)} />}
      {confirmando && <ConfirmarReleitura onFechar={() => setConfirmando(false)} />}
    </section>
  );
}

/**
 * A conta fechada: "N caminhos (D duplicados) → X documentos = V visitas + S sem visita" e, com as evidências
 * complementares (ADR-048), "… = V visitas + E evidências ligadas a visitas + S sem visita" — como a API soma
 * (`documentos = visitas + evidenciasLigadas + semVisita.total`).
 */
function linhaDaConta(conta: DocumentosSemVisita): string {
  const parcelas = [`${numero.format(conta.visitas)} visitas`];
  if (conta.evidenciasLigadas !== undefined) {
    const n = conta.evidenciasLigadas;
    parcelas.push(`${numero.format(n)} ${n === 1 ? 'evidência ligada' : 'evidências ligadas'} a visitas`);
  }
  parcelas.push(`${numero.format(conta.semVisita.total)} sem visita`);
  return (
    `${numero.format(conta.caminhos)} caminhos (${numero.format(conta.caminhosDuplicados)} duplicados) → ` +
    `${numero.format(conta.documentos)} documentos = ${parcelas.join(' + ')}`
  );
}

/** Os motivos que a API mandou, na ordem do quadro (o motivo novo fica de fora numa API anterior — ADR-048). */
function motivosDaConta(conta: DocumentosSemVisita): [MotivoSemVisita, number][] {
  return MOTIVOS_SEM_VISITA.flatMap((m): [MotivoSemVisita, number][] => {
    const qtd = conta.semVisita[m];
    return qtd === undefined ? [] : [[m, qtd]];
  });
}

type PlanilhasDoPainel = NonNullable<DocumentosSemVisita['planilhas']>;

function totalDePlanilhas(p: PlanilhasDoPainel): number {
  return p.importadas + p.recusadas + (p.naoReconhecidas ?? 0) + p.emAnalise;
}

/** A linha das planilhas; "não reconhecida(s)" só quando a API manda o campo (Req 1.4 — a API anterior segue igual). */
function linhaDasPlanilhas(p: PlanilhasDoPainel): string {
  const partes = [`${numero.format(p.importadas)} importada(s)`, `${numero.format(p.recusadas)} recusada(s)`];
  if (p.naoReconhecidas !== undefined) partes.push(`${numero.format(p.naoReconhecidas)} não reconhecida(s)`);
  partes.push(`${numero.format(p.emAnalise)} em análise`);
  return `Planilhas (fonte de visitas, fora desta conta): ${partes.join(' · ')}`;
}

